import React, { useState, useEffect, useCallback } from 'react';
import { psychometricApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import './PsychometricOnboarding.css';

const LIKERT_LABELS = ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'];

const PsychometricOnboarding = () => {
  const { user, updateUser } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [responses, setResponses] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [phase, setPhase] = useState('intro');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const [oneLiner, setOneLiner] = useState('');
  const [typedText, setTypedText] = useState('');
  const [typingDone, setTypingDone] = useState(false);

  useEffect(() => {
    if (phase !== 'complete' || !oneLiner) return;
    let i = 0;
    const chars = oneLiner.split('');
    const type = () => {
      if (i >= chars.length) { setTypingDone(true); return; }
      setTypedText(oneLiner.slice(0, i + 1));
      const ch = chars[i];
      const delay = ch === '.' || ch === '\'' ? 120 : ch === ',' ? 80 : ch === ' ' ? 50 : 35;
      i++;
      setTimeout(type, delay);
    };
    const startDelay = setTimeout(type, 1800);
    return () => clearTimeout(startDelay);
  }, [phase, oneLiner]);

  useEffect(() => {
    const loadQuestions = async () => {
      try {
        const data = await psychometricApi.getQuestions();
        setQuestions(data.questions);
        setLoading(false);
      } catch (err) {
        setError('Failed to load assessment. Please refresh.');
        setLoading(false);
      }
    };
    loadQuestions();
  }, []);

  const handleNext = () => {
    if (selectedAnswer === null || selectedAnswer === undefined) return;

    const question = questions[currentIndex];
    let response;

    if (question.type === 'mcq' || !question.type) {
      response = { questionId: question.id, answerId: selectedAnswer };
    } else if (question.type === 'likert') {
      response = { questionId: question.id, value: selectedAnswer };
    } else {
      response = { questionId: question.id, answer: selectedAnswer };
    }

    const newResponses = [...responses, response];
    setResponses(newResponses);
    setSelectedAnswer(null);

    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      submitAssessment(newResponses);
    }
  };

  const submitAssessment = async (allResponses) => {
    setPhase('analyzing');
    try {
      await psychometricApi.submit(allResponses);
      pollForCompletion();
    } catch (err) {
      setError('Failed to submit assessment. Please try again.');
      setPhase('questions');
    }
  };

  const pollForCompletion = useCallback(async () => {
    try {
      const data = await psychometricApi.getStatus();
      if (data.completed) {
        try {
          const profileData = await psychometricApi.getProfile();
          if (profileData.profile?.one_liner) setOneLiner(profileData.profile.one_liner);
        } catch {}
        setPhase('complete');
      } else {
        setPollCount(prev => {
          if (prev < 60) {
            setTimeout(pollForCompletion, 2000);
          } else {
            setError('Analysis is taking longer than expected. Please refresh the page.');
          }
          return prev + 1;
        });
      }
    } catch {
      setPollCount(prev => {
        if (prev < 30) setTimeout(pollForCompletion, 3000);
        return prev + 1;
      });
    }
  }, [updateUser, user]);

  const startAssessment = () => {
    setPhase('questions');
  };

  const renderMCQ = (question) => (
    <div className="psych-options">
      {question.options.map((option, idx) => (
        <button
          key={option.id}
          className={`psych-option ${selectedAnswer === option.id ? 'selected' : ''}`}
          onClick={() => setSelectedAnswer(option.id)}
        >
          <span className="psych-option-letter">
            {String.fromCharCode(65 + idx)}
          </span>
          <span className="psych-option-text">{option.text}</span>
        </button>
      ))}
    </div>
  );

  const renderTrueFalse = (question) => (
    <div className="psych-tf-options">
      <button
        className={`psych-tf-btn ${selectedAnswer === true ? 'selected' : ''}`}
        onClick={() => setSelectedAnswer(true)}
      >
        <span className="psych-tf-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </span>
        <span className="psych-tf-label">True</span>
        <span className="psych-tf-sub">This sounds like me</span>
      </button>
      <button
        className={`psych-tf-btn ${selectedAnswer === false ? 'selected' : ''}`}
        onClick={() => setSelectedAnswer(false)}
      >
        <span className="psych-tf-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </span>
        <span className="psych-tf-label">False</span>
        <span className="psych-tf-sub">Not really me</span>
      </button>
    </div>
  );

  const renderLikert = (question) => {
    const labels = question.likert_labels || LIKERT_LABELS;
    return (
      <div className="psych-likert">
        <div className="psych-likert-track">
          {labels.map((label, idx) => {
            const value = idx + 1;
            const isSelected = selectedAnswer === value;
            return (
              <button
                key={value}
                className={`psych-likert-point ${isSelected ? 'selected' : ''}`}
                onClick={() => setSelectedAnswer(value)}
              >
                <span className="psych-likert-dot" />
                <span className="psych-likert-label">{label}</span>
              </button>
            );
          })}
        </div>
        <div className="psych-likert-ends">
          <span>{labels[0]}</span>
          <span>{labels[labels.length - 1]}</span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="psych-onboarding">
        <div className="psych-loading">
          <div className="psych-spinner" />
          <p>Crafting your personalized assessment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="psych-onboarding">
        <div className="psych-error">
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Refresh</button>
        </div>
      </div>
    );
  }

  if (phase === 'intro') {
    return (
      <div className="psych-onboarding">
        <div className="psych-intro">
          <div className="psych-intro-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" />
              <path d="M12 6v6l4 2" />
            </svg>
          </div>
          <h1>Before we begin...</h1>
          <p className="psych-intro-subtitle">
            I've prepared {questions.length} questions about how you connect, love, and navigate
            relationships. No right or wrong answers — just go with your gut.
          </p>
          <div className="psych-intro-details">
            <div className="psych-intro-detail">
              <span className="psych-detail-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              </span>
              <span>Takes about 3 minutes</span>
            </div>
            <div className="psych-intro-detail">
              <span className="psych-detail-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </span>
              <span>Your answers are private and encrypted</span>
            </div>
            <div className="psych-intro-detail">
              <span className="psych-detail-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
              </span>
              <span>Personalized to your unique relational patterns</span>
            </div>
          </div>
          <button className="psych-start-btn" onClick={startAssessment}>
            Let's do this
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'analyzing') {
    return (
      <div className="psych-onboarding">
        <div className="psych-analyzing">
          <div className="psych-brain-animation">
            <div className="psych-brain-ring ring-1" />
            <div className="psych-brain-ring ring-2" />
            <div className="psych-brain-ring ring-3" />
            <div className="psych-brain-core">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 2a7 7 0 0 0-7 7c0 2.38 1.19 4.47 3 5.74V17a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-2.26c1.81-1.27 3-3.36 3-5.74a7 7 0 0 0-7-7z" />
                <line x1="9" y1="21" x2="15" y2="21" />
              </svg>
            </div>
          </div>
          <h2>Understanding you...</h2>
          <p>I'm analyzing your responses to build a deep understanding of who you are.</p>
          <div className="psych-analyzing-steps">
            <div className={`psych-step ${pollCount > 0 ? 'active' : ''}`}>
              <span className="step-dot" />
              Reading your relational patterns
            </div>
            <div className={`psych-step ${pollCount > 3 ? 'active' : ''}`}>
              <span className="step-dot" />
              Understanding how you love and repair
            </div>
            <div className={`psych-step ${pollCount > 6 ? 'active' : ''}`}>
              <span className="step-dot" />
              Building your relational profile
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'complete') {
    return (
      <div className="psych-onboarding">
        <div className="psych-complete">
          <div className="psych-checkmark-soft reveal reveal-1">
            <svg viewBox="0 0 24 24" fill="none" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="var(--secondary-accent-dim)" />
              <polyline points="22 4 12 14.01 9 11.01" stroke="var(--warm-terracotta)" />
            </svg>
          </div>
          <h2 className="psych-complete-heading reveal reveal-2">I see you now.</h2>
          {oneLiner && (
            <p className={`psych-one-liner-typed ${typingDone ? 'done' : ''}`}>
              {typedText ? `"${typedText}"` : ''}
              {typedText && !typingDone && <span className="typing-cursor">|</span>}
              {typingDone && <span className="typing-cursor blink-out">|</span>}
            </p>
          )}
          <div className={`psych-complete-divider ${typingDone ? 'reveal-in' : ''}`} />
          <p className={`psych-complete-support ${typingDone ? 'reveal-in' : ''}`}>Every conversation from here will be shaped by how you connect — your patterns, your repair style, your heart.</p>
          <button className={`psych-ghost-btn ${typingDone ? 'reveal-in' : ''}`} onClick={() => updateUser({ ...user, psychometric_completed: true })}>
            I'm ready
          </button>
        </div>
      </div>
    );
  }

  const question = questions[currentIndex];
  const progress = ((currentIndex) / questions.length) * 100;
  const questionType = question.type || 'mcq';
  const TYPE_LABELS = { mcq: 'Choose one', likert: 'How much does this feel like you?', true_false: 'True or false' };
  const typeLabel = TYPE_LABELS[questionType] || 'Choose one';

  return (
    <div className="psych-onboarding">
      <div className="psych-question-container">
        <div className="psych-progress">
          <div className="psych-progress-bar" style={{ width: `${progress}%` }} />
          <span className="psych-progress-text">{currentIndex + 1} of {questions.length}</span>
        </div>

        <div className="psych-question" key={question.id}>
          <span className="psych-question-type">{typeLabel}</span>
          <h2 className="psych-question-text">{question.text}</h2>

          {questionType === 'mcq' && renderMCQ(question)}
          {questionType === 'true_false' && renderTrueFalse(question)}
          {questionType === 'likert' && renderLikert(question)}
          <div className="psych-nav">
            <button
              className="psych-next-btn"
              onClick={handleNext}
              disabled={selectedAnswer === null || selectedAnswer === undefined}
            >
              {currentIndex === questions.length - 1 ? 'Finish' : 'Next'}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PsychometricOnboarding;
